import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { Competition } from '../../types/interfaces';
import { CompetitionRole, errorMessage, uploadLogo } from '../../services/manageApi';
import ReadOnlyField from './ReadOnlyField';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

interface Props {
  competition: Competition;
  canEdit: boolean;
  requiredRole?: CompetitionRole;
  onUploaded: (url: string) => void;
}

const LogoField: React.FC<Props> = ({ competition, canEdit, requiredRole, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState('');
  const pathId = (competition.path_id || '').trim();

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  if (!canEdit) {
    return (
      <ReadOnlyField
        label="Logo"
        value={competition.logo_path ? <img src={competition.logo_path} alt="Current logo" className="manage-logo-preview" /> : ''}
        requiredRole={requiredRole}
      />
    );
  }

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] || null;
    event.target.value = '';
    setError('');
    if (!chosen) return;
    if (chosen.type !== 'image/png' && !chosen.name.toLowerCase().endsWith('.png')) {
      setError('Logo must be a PNG image.');
      return;
    }
    if (chosen.size > MAX_LOGO_BYTES) {
      setError('Logo must be at most 2 MB.');
      return;
    }
    setFile(chosen);
    setPreview(typeof URL.createObjectURL === 'function' ? URL.createObjectURL(chosen) : '');
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await uploadLogo(pathId, file);
      onUploaded(result.public_url);
      setFile(null);
      setPreview('');
      setToast('Logo updated');
    } catch (err) {
      setError(errorMessage(err, 'Failed to upload logo'));
    } finally {
      setUploading(false);
    }
  };

  const shownLogo = preview || competition.logo_path || '';

  return (
    <div className="manage-field" data-testid="logo-field">
      <span className="manage-field__label">Logo</span>
      {!pathId ? (
        <p className="manage-field__help">This competition has no asset folder yet. Ask a platform admin to set one up.</p>
      ) : (
        <div className="manage-logo">
          {shownLogo && <img src={shownLogo} alt={preview ? 'New logo preview' : 'Current logo'} className="manage-logo-preview" />}
          <label className="btn btn--secondary btn--sm">
            Choose PNG…
            <input type="file" accept="image/png" hidden aria-label="Choose logo file" onChange={choose} />
          </label>
          <Button variant="primary" size="sm" loading={uploading} disabled={!file || uploading} onClick={upload}>
            Upload logo
          </Button>
          <span className="manage-field__help">PNG, at most 2 MB.</span>
        </div>
      )}
      {error && <p className="manage-error" role="alert">{error}</p>}
      {toast && <Toast message={toast} duration={2500} onClose={() => setToast('')} />}
    </div>
  );
};

export default LogoField;
